import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./Header.css";
import logo from "../Images/logo.png";
import { FaBars, FaTimes } from "react-icons/fa";
import {
    FaCalendarAlt,
    FaUsers,
    FaCamera,
    FaBuilding,
    FaUserShield,
    FaUserCircle,
    FaSignOutAlt,
} from "react-icons/fa";

const Header: React.FC = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const menuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const roles = userRoles || [];
    const isAdmin = roles.includes("Admin");

    const hasRole = (role: string) => isAdmin || roles.includes(role);
    const hasAnyRole = (allowed: string[]) =>
        isAdmin || allowed.some((r) => roles.includes(r));

    // EVENTS må IKKE vises for Team / Media
    const canSeeEvents = hasAnyRole(["User", "Volunteer", "Club", "Champ"]);

    const loadAuthState = () => {
        setUserEmail(sessionStorage.getItem("userEmail"));
        setUserRoles(JSON.parse(sessionStorage.getItem("userRoles") || "[]"));
    };

    useEffect(() => {
        loadAuthState();
        const listener = () => loadAuthState();
        window.addEventListener("auth-changed", listener);
        return () => window.removeEventListener("auth-changed", listener);
    }, []);

    const toggleMenu = () => setMenuOpen((v) => !v);
    const closeMenu = () => setMenuOpen(false);

    const handleLogout = () => {
        sessionStorage.clear();
        window.dispatchEvent(new Event("auth-changed"));
        closeMenu();
        navigate("/login");
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                closeMenu();
            }
        };
        if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [menuOpen]);

    return (
        <header className="header">
            <div className="header-logo" onClick={() => navigate("/")}>
                <img src={logo} alt="Logo" />
            </div>

            <button className="burger-menu" onClick={toggleMenu}>
                {menuOpen ? <FaTimes /> : <FaBars />}
            </button>

            <div
                className={`nav-overlay ${menuOpen ? "show" : ""}`}
                onClick={closeMenu}
            />

            <nav className={`header-nav ${menuOpen ? "open" : ""}`} ref={menuRef}>
                {/* LEFT: Main */}
                <div className="nav-left">
                    {canSeeEvents && (
                        <NavLink to="/" onClick={closeMenu}>
                            <FaCalendarAlt className="nav-icon" />
                            EVENTS
                        </NavLink>
                    )}

                    {hasRole("Team") && (
                        <NavLink to="/Team" onClick={closeMenu}>
                            <FaUsers className="nav-icon" />
                            TEAM
                        </NavLink>
                    )}

                    {hasRole("Media") && (
                        <NavLink to="/Media" onClick={closeMenu}>
                            <FaCamera className="nav-icon" />
                            MEDIA
                        </NavLink>
                    )}

                    {hasRole("Club") && (
                        <NavLink to="/Club" onClick={closeMenu}>
                            <FaBuilding className="nav-icon" />
                            KLUB
                        </NavLink>
                    )}
                    {hasRole("User") && (
                        <NavLink to="/volunteer-groups" onClick={closeMenu}>
                            <FaUsers className="nav-icon" />
                            FRIVILLIG GRUPPER
                        </NavLink>
                    )}
                    {hasRole("Champ") && (
                        <NavLink to="/Champ" onClick={closeMenu}>
                            <FaBuilding className="nav-icon" />
                            CHAMPOFFICE
                        </NavLink>
                    )}
                </div>

                {/* RIGHT: Admin + Account */}
                <div className="nav-right">
                    {hasRole("Admin") && (
                        <NavLink to="/Admin" onClick={closeMenu} className="admin-link">
                            <FaUserShield className="nav-icon" />
                            ADMINISTRATION
                        </NavLink>
                    )}

                    <div className="nav-divider-vertical" />

                    {roles.length === 0 ? (
                        <NavLink to="/login" onClick={closeMenu}>
                            LOGIN
                        </NavLink>
                    ) : (
                        <>
                            <NavLink
                                to="/UserDashboard"
                                className="icon-btn"
                                title={userEmail ?? "Profil"}
                                onClick={closeMenu}
                            >
                                <FaUserCircle />
                            </NavLink>

                            <button
                                className="icon-btn logout"
                                onClick={handleLogout}
                                title="Log ud"
                            >
                                <FaSignOutAlt />
                            </button>
                        </>
                    )}
                </div>
            </nav>

        </header>
    );
};

export default Header;
